import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Search, MoreHorizontal, X as XIcon, Loader2, ArrowDownAZ, Clock,
    ChevronDown, ChevronRight, Ban, UserPlus, Bell, Maximize2, Minimize2, Users,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PanelShell from './PanelShell';
import apiService from '../../services/apiService';
import contactService from '../../services/contactService';
import teamService from '../../services/teamService';
import DocumentPreviewModal from '../DocumentPreviewModal';
import { useLanguage } from '../../i18n/index.jsx';
import { ManualContactDialog } from '../contacts/contactForm.jsx';
import ContactRow from '../contacts/ContactRow.jsx';
import GhostCard, { EMAIL_RE } from '../contacts/GhostCard.jsx';
import ContactEditOverlay from '../contacts/ContactEditOverlay.jsx';
import { PendingPortal, BlockListModal } from '../contacts/contactModals.jsx';
import { TeamRow, TeamCreateDialog } from '../contacts/teamsTier.jsx';
import { userErrorMessage } from '../../utils/userError';

// ── localStorage prefs (Contacts Hub — Phase B) ────────────────────────
const SORT_KEY = 'omnis:contacts:sort';
const TIERS_KEY = 'omnis:contacts:tiers';

const readSort = () => {
    try { return localStorage.getItem(SORT_KEY) === 'alphabetical' ? 'alphabetical' : 'recent'; }
    catch (_) { return 'recent'; }
};
const writeSort = (v) => { try { localStorage.setItem(SORT_KEY, v); } catch (_) { /* ignore */ } };
const readTiers = () => {
    try { const o = JSON.parse(localStorage.getItem(TIERS_KEY)); return o && typeof o === 'object' ? o : {}; }
    catch (_) { return {}; }
};
const writeTiers = (o) => { try { localStorage.setItem(TIERS_KEY, JSON.stringify(o)); } catch (_) { /* ignore */ } };

const tsOfContact = (c) => Date.parse(c?.updated_at || c?.created_at || 0) || 0;

const sortContacts = (list, mode) => {
    const arr = [...list];
    if (mode === 'alphabetical') {
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    } else {
        arr.sort((a, b) => tsOfContact(b) - tsOfContact(a));
    }
    return arr;
};

const matchesQuery = (c, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [
        c.name,
        c.company,
        c.email,
        ...(Array.isArray(c.emails) ? c.emails : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(needle);
};

// Team word-picture: match on name or description (Phase C).
const teamMatchesQuery = (t, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return [t.name, t.description].filter(Boolean).join(' ').toLowerCase().includes(needle);
};

// Alphabetical letter-group headers (only in alphabetical mode).
const letterGroups = (list) => {
    const groups = new Map();
    for (const c of list) {
        const letter = (c.name || '?')[0]?.toUpperCase() || '#';
        const key = /^[A-Z]$/.test(letter) ? letter : '#';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

// ── A single collapsible tier container ────────────────────────────────
const TierSection = ({
    tierKey, title, count, collapsed, onToggleCollapse, forceExpanded,
    emptyLabel, rows, sortMode, renderRow,
}) => {
    const expanded = forceExpanded || !collapsed;
    return (
        <section data-testid={`tier-${tierKey}`} className="rounded-card border border-pai bg-white/[0.03] overflow-hidden">
            <button
                type="button"
                data-testid={`tier-header-${tierKey}`}
                onClick={onToggleCollapse}
                className="w-full px-[22px] py-[17px] flex items-center gap-3 text-left hover:bg-white/[0.04] transition-colors duration-150"
            >
                {expanded
                    ? <ChevronDown size={18} className="text-white/60 flex-shrink-0" />
                    : <ChevronRight size={18} className="text-white/60 flex-shrink-0" />}
                <span
                    data-testid={`tier-title-${tierKey}`}
                    className="flex-1 min-w-0 truncate text-[17px] font-bold tracking-[-0.01em] text-white"
                >
                    {title}
                </span>
                <span className="text-[14px] font-medium text-pai-meta tnum flex-shrink-0">
                    {count}
                </span>
            </button>
            {expanded && (
                <div data-testid={`tier-body-${tierKey}`} className="border-t border-white/[0.07]">
                    {rows.length === 0 ? (
                        <p className="px-[22px] py-4 text-[13px] text-pai-faint italic">{emptyLabel}</p>
                    ) : sortMode === 'alphabetical' ? (
                        letterGroups(rows).map(([letter, group]) => (
                            <div key={letter}>
                                <div className="px-[22px] py-1.5 pai-eyebrow bg-white/[0.02] border-b border-pai-hairline">
                                    {letter}
                                </div>
                                {group.map((c) => renderRow(c))}
                            </div>
                        ))
                    ) : (
                        rows.map((c) => renderRow(c))
                    )}
                </div>
            )}
        </section>
    );
};

// ── Main panel ─────────────────────────────────────────────────────────
const AgendaPanel = ({ onClose, mode, onToggleExpand, user }) => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();

    const [contacts, setContacts] = useState([]);
    const [linkedUserIds, setLinkedUserIds] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    // Search-box focus drives the responsive command bar (Search box contacts #231):
    // while active the search input expands (the title truncates/yields its space and
    // the sort label collapses) so the input stays clearly usable at the minimum
    // responsive panel width — without ever overlapping the sort/order button.
    const [searchFocused, setSearchFocused] = useState(false);
    const [sortMode, setSortMode] = useState(readSort);
    const [collapsedTiers, setCollapsedTiers] = useState(readTiers);
    const [expandedId, setExpandedId] = useState(null);
    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [showCreateTeam, setShowCreateTeam] = useState(false);

    // The viewer's DB user id (== Firebase UID). useAuth's `user` carries `.id`
    // (the /auth/profile shape — NOT a raw Firebase user, so no `.uid`); tolerate
    // `.uid` defensively and, if the prop is absent (e.g. auth still settling or
    // a caller that doesn't thread it), resolve once via GET /auth/profile. The
    // leave/self logic must NEVER see a null id (teams.spec.js 5b regression:
    // leave used to fire DELETE /api/teams/<id>/members/null → 404).
    const [profileUserId, setProfileUserId] = useState(null);
    const currentUserId = user?.id || user?.uid || profileUserId;

    useEffect(() => {
        if (currentUserId) return undefined;
        let cancelled = false;
        apiService.getUserProfile()
            .then((p) => { if (!cancelled && p?.id) setProfileUserId(p.id); })
            .catch(() => { /* leave handler has its own fallback */ });
        return () => { cancelled = true; };
    }, [currentUserId]);

    // Discovery (email-shaped search → Ghost Card).
    const [discovery, setDiscovery] = useState(null); // {state,email,requestId?,contactId?,error?}
    const [discovering, setDiscovering] = useState(false);
    const [ghostBusy, setGhostBusy] = useState(false);

    // Modals / overlays.
    const [showCreate, setShowCreate] = useState(false);
    const [showPending, setShowPending] = useState(false);
    const [showBlockList, setShowBlockList] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);

    const menuRef = useRef(null);

    // Container-aware "compact" command bar (Search box contacts #231). A docked
    // split panel can be narrow while the VIEWPORT is wide, so a viewport-keyed
    // `md:` breakpoint is the wrong signal — a ResizeObserver on the command bar
    // itself drives `compact`, which collapses the sort button's text label so the
    // search box always has room and never collides with the sort/order button.
    const barRef = useRef(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        const el = barRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect?.width ?? el.clientWidth;
                setCompact(w < 440);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => { writeSort(sortMode); }, [sortMode]);
    useEffect(() => { writeTiers(collapsedTiers); }, [collapsedTiers]);

    // ── Data fetch ─────────────────────────────────────────────────────
    const fetchContacts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await contactService.listContacts({});
            setContacts(res.contacts || []);
            setLinkedUserIds(res.linked_user_ids || []);
        } catch (err) {
            setError(userErrorMessage(err, t));
            setContacts([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    // THE TEAMS tier data source (Phase C).
    const fetchTeams = useCallback(async () => {
        try {
            const res = await teamService.listTeams();
            setTeams(res.teams || []);
        } catch (_) { /* keep last known list on a transient error */ }
    }, []);

    const fetchPendingCount = useCallback(async () => {
        try {
            const res = await contactService.listRequests();
            setPendingCount((res.received || []).length);
        } catch (_) { /* keep last known */ }
    }, []);

    useEffect(() => { fetchContacts(); fetchTeams(); fetchPendingCount(); }, [fetchContacts, fetchTeams, fetchPendingCount]);

    // Chat-driven tools + bell Accept/Decline dispatch omnis:contact-changed.
    useEffect(() => {
        const handler = () => { fetchContacts(); fetchPendingCount(); };
        window.addEventListener('omnis:contact-changed', handler);
        return () => window.removeEventListener('omnis:contact-changed', handler);
    }, [fetchContacts, fetchPendingCount]);

    // Chat-driven team tools + the tier's own mutations dispatch omnis:team-changed.
    useEffect(() => {
        const handler = () => { fetchTeams(); };
        window.addEventListener('omnis:team-changed', handler);
        return () => window.removeEventListener('omnis:team-changed', handler);
    }, [fetchTeams]);

    // Deep link: /agenda?requests=1 (bell "New Contact Request") opens the portal.
    useEffect(() => {
        if (searchParams.get('requests') === '1') {
            setShowPending(true);
            const next = new URLSearchParams(searchParams);
            next.delete('requests');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Close the three-dots menu on outside click.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    // ── Discovery: debounced email lookup ──────────────────────────────
    const runDiscovery = useCallback(async (email, extraError) => {
        try {
            const res = await contactService.discover(email);
            setDiscovery({
                state: res?.state || 'not_found',
                email,
                requestId: res?.request_id || null,
                contactId: res?.contact_id || null,
                error: extraError || null,
            });
        } catch (_) {
            setDiscovery({ state: 'not_found', email, error: extraError || null });
        }
    }, []);

    useEffect(() => {
        const q = search.trim();
        if (!EMAIL_RE.test(q)) { setDiscovery(null); setDiscovering(false); return undefined; }
        setDiscovering(true);
        const timer = setTimeout(async () => {
            await runDiscovery(q);
            setDiscovering(false);
        }, 400);
        return () => { clearTimeout(timer); setDiscovering(false); };
    }, [search, runDiscovery]);

    const discoverErrMessage = useCallback((err) => {
        const code = err?.message || '';
        if (code === 'already_requested') return t('discoverErrAlreadyRequested');
        if (code === 'already_connected') return t('discoverErrAlreadyConnected');
        if (code === 'they_requested_you') return t('discoverErrTheyRequestedYou');
        // A human BE string ("No PAI user found.", "Enter a valid email address.") passes
        // through; anything technical becomes the standard sentence (#439).
        return userErrorMessage(err, t);
    }, [t]);

    // ── Ghost Card handlers ────────────────────────────────────────────
    const handleGhostSend = useCallback(async () => {
        if (!discovery?.email) return;
        setGhostBusy(true);
        try {
            const res = await contactService.sendRequest(discovery.email);
            setDiscovery((d) => ({ ...d, state: 'pending_sent', requestId: res?.request?.id || d?.requestId, error: null }));
            fetchPendingCount();
        } catch (err) {
            // Re-derive the authoritative state (they_requested_you → pending_received,
            // already_connected → linked, already_requested → pending_sent) + show why.
            await runDiscovery(discovery.email, discoverErrMessage(err));
        } finally {
            setGhostBusy(false);
        }
    }, [discovery, fetchPendingCount, runDiscovery, discoverErrMessage]);

    const handleGhostCancel = useCallback(async () => {
        if (!discovery?.requestId) return;
        setGhostBusy(true);
        try {
            await contactService.cancelRequest(discovery.requestId);
            await runDiscovery(discovery.email);
            fetchPendingCount();
        } catch (err) {
            setDiscovery((d) => ({ ...d, error: discoverErrMessage(err) }));
        } finally {
            setGhostBusy(false);
        }
    }, [discovery, runDiscovery, fetchPendingCount, discoverErrMessage]);

    const handleGhostRespond = useCallback(async (decision) => {
        if (!discovery?.requestId) return;
        setGhostBusy(true);
        try {
            await contactService.respondRequest(discovery.requestId, decision);
            await Promise.all([fetchContacts(), fetchPendingCount()]);
            await runDiscovery(discovery.email);
        } catch (err) {
            setDiscovery((d) => ({ ...d, error: discoverErrMessage(err) }));
        } finally {
            setGhostBusy(false);
        }
    }, [discovery, fetchContacts, fetchPendingCount, runDiscovery, discoverErrMessage]);

    const handleGhostGoToLinked = useCallback(() => {
        const id = discovery?.contactId;
        setSearch('');
        setDiscovery(null);
        if (id) {
            setExpandedId(id);
            // Scroll the now-expanded linked contact into view (FE-6 "scrolls to").
            setTimeout(() => {
                try { document.querySelector(`[data-contact-id="${id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
                catch (_) { /* ignore */ }
            }, 80);
        }
    }, [discovery]);

    // ── Favorites toggle ───────────────────────────────────────────────
    const handleToggleFavorite = useCallback(async (contact) => {
        const next = !contact.is_favorite;
        setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, is_favorite: next } : c)));
        try {
            await contactService.toggleFavorite(contact.id, next);
        } catch (_) {
            fetchContacts();
        }
    }, [fetchContacts]);

    // ── Tier collapse toggle (independent per tier) ────────────────────
    const toggleTier = (key) => setCollapsedTiers((s) => ({ ...s, [key]: !s[key] }));

    // ── Derived tier lists (sorted + filtered) ─────────────────────────
    const query = search.trim();
    const filtered = useMemo(() => {
        const base = query ? contacts.filter((c) => matchesQuery(c, query)) : contacts;
        return sortContacts(base, sortMode);
    }, [contacts, query, sortMode]);

    const networkRows = useMemo(() => filtered.filter((c) => c.tier === 'network'), [filtered]);
    const rolodexRows = useMemo(() => filtered.filter((c) => c.tier !== 'network'), [filtered]);

    // THE TEAMS tier — word-picture filtered (name/description) + sorted like the
    // other tiers (recent by created_at / alphabetical by name — Phase C, T2 bits).
    const networkContacts = useMemo(() => contacts.filter((c) => c.tier === 'network'), [contacts]);
    const teamRows = useMemo(() => {
        const base = query ? teams.filter((tm) => teamMatchesQuery(tm, query)) : teams;
        return sortContacts(base, sortMode);
    }, [teams, query, sortMode]);

    const renderRow = useCallback((c) => (
        <ContactRow
            key={c.id}
            contact={c}
            expanded={expandedId === c.id}
            onToggle={() => setExpandedId((cur) => (cur === c.id ? null : c.id))}
            onToggleFavorite={handleToggleFavorite}
            onEdit={(ct) => setEditingContact(ct)}
            navigate={navigate}
            onPreviewDoc={(d) => setPreviewDoc({ id: d.id, filename: d.filename || 'Document' })}
        />
    ), [expandedId, handleToggleFavorite, navigate]);

    const renderTeamRow = useCallback((tm) => (
        <TeamRow
            key={tm.id}
            team={tm}
            expanded={expandedTeamId === tm.id}
            onToggle={() => setExpandedTeamId((cur) => (cur === tm.id ? null : tm.id))}
            networkContacts={networkContacts}
            linkedIds={linkedUserIds}
            currentUserId={currentUserId}
            navigate={navigate}
            onPreviewDoc={(d) => setPreviewDoc({ id: d.id, filename: d.filename || 'Document' })}
            onChanged={fetchTeams}
        />
    ), [expandedTeamId, networkContacts, linkedUserIds, currentUserId, navigate, fetchTeams]);

    // When filtering, a tier with ZERO matches vanishes entirely (header included).
    const searching = !!query;
    const showNetwork = !searching || networkRows.length > 0;
    const showTeams = !searching || teamRows.length > 0;
    const showRolodex = !searching || rolodexRows.length > 0;

    const noContactsAtAll = !loading && contacts.length === 0;

    return (
        <PanelShell {...{ mode, onClose, onToggleExpand }} noChromeHeader>
            <div className="relative h-full flex flex-col">
                {/* ── Command Bar (single row) ── */}
                {/* Robust flex layout (Search box contacts #231): the title is shrinkable +
                    truncates (min-w-0 truncate) and yields its space entirely when the search
                    is focused; the search wrapper grows (flex-1) but has a min-width floor so
                    its padded input can never spill; the icon buttons are shrink-0 and the sort
                    label collapses when the bar is narrow (compact) or the search is active.
                    gap-2 guarantees a permanent gap so the search box can NEVER overlap the
                    sort/order button at any panel width. */}
                {/* px-3 is load-bearing, not taste: at the minimum split-panel width
                    search-box-contacts.spec.js measures that focusing the search GROWS
                    it (the title yields its max-w-[9rem]). The bar is already at its
                    min-content budget there, so widening the padding eats the very
                    slack that growth comes from. Restyle colours here, not the box. */}
                {/* #432: on a finger the five header buttons are 40px each, which leaves a
                    380px docked panel no room for the title AND the search in one row
                    (the title was crushed to "Cont…"). There the bar WRAPS: title +
                    buttons on the first line, the search full-width underneath. */}
                <div ref={barRef} className="flex items-center gap-2 touch:flex-wrap px-3 py-3 border-b border-pai-subtle flex-shrink-0">
                    <h1
                        data-testid="hub-title"
                        className={`text-[22px] font-bold tracking-[-0.02em] text-white min-w-0 truncate touch:flex-1 transition-all duration-200 ease-out ${
                            searchFocused ? 'max-w-0 opacity-0' : 'max-w-[9rem] opacity-100'
                        }`}
                    >
                        {t('contactsTitle')}
                    </h1>

                    {/* min-w-[5rem] is the hard floor: the input carries pl-9 pr-8 (~68px of
                        padding for the icon + clear button), so a wrapper any narrower would
                        let the input spill out and overlap the sort button — the actual bug. */}
                    <div className="relative flex-1 min-w-[5rem] touch:order-last touch:basis-full transition-all duration-200 ease-out">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none z-10" />
                        <input
                            data-testid="hub-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onFocus={() => setSearchFocused(true)}
                            onBlur={() => setSearchFocused(false)}
                            placeholder={t('contactsSearchPlaceholder')}
                            className="pai-input w-full pl-9 pr-8 py-2 text-sm"
                        />
                        {discovering ? (
                            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-pai-faint animate-spin" />
                        ) : search ? (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-pai-faint hover:text-white p-0.5 transition-colors"
                                aria-label={t('auxClearSearch', 'Clear search')}
                            >
                                <XIcon size={12} />
                            </button>
                        ) : null}
                    </div>

                    <button
                        data-testid="hub-sort-toggle"
                        onClick={() => setSortMode((m) => (m === 'recent' ? 'alphabetical' : 'recent'))}
                        title={t('contactsSortTooltip')}
                        aria-label={t('contactsSortTooltip')}
                        data-sort={sortMode}
                        className="pai-tap justify-center flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-ctl shrink-0 transition-colors"
                    >
                        {sortMode === 'alphabetical' ? <ArrowDownAZ size={16} /> : <Clock size={16} />}
                        {/* Label collapses when the bar is narrow (compact) or the search is
                            active — freeing space so the search box stays clear of this button. */}
                        <span className={compact || searchFocused ? 'hidden' : 'inline'}>{sortMode === 'alphabetical' ? t('contactsSortAlpha') : t('contactsSortRecent')}</span>
                    </button>

                    <button
                        data-testid="hub-pending-button"
                        onClick={() => setShowPending(true)}
                        title={t('contactsPending')}
                        aria-label={t('contactsPending')}
                        className="pai-tap justify-center relative p-1.5 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors shrink-0"
                    >
                        <Bell size={16} />
                        {pendingCount > 0 && (
                            <span
                                data-testid="hub-pending-badge"
                                className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-pai-red/[0.18] border border-pai-red/40 text-pai-red text-[9px] font-bold flex items-center justify-center tnum"
                            >
                                {pendingCount}
                            </span>
                        )}
                    </button>

                    <div className="relative shrink-0" ref={menuRef}>
                        <button
                            data-testid="hub-menu-button"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-label={t('auxContactsMenu', 'Contacts menu')}
                            className="pai-tap justify-center p-1.5 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors"
                        >
                            <MoreHorizontal size={16} />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-9 z-30 w-44 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1">
                                <button
                                    data-testid="hub-menu-new-contact"
                                    onClick={() => { setMenuOpen(false); setShowCreate(true); }}
                                    className="w-full text-left px-3 py-2 text-xs text-pai-secondary hover:text-white hover:bg-white/[0.07] flex items-center gap-2 transition-colors"
                                >
                                    <UserPlus size={13} /> {t('contactsMenuNewContact')}
                                </button>
                                <button
                                    data-testid="hub-menu-new-team"
                                    onClick={() => { setMenuOpen(false); setShowCreateTeam(true); }}
                                    className="w-full text-left px-3 py-2 text-xs text-pai-secondary hover:text-white hover:bg-white/[0.07] flex items-center gap-2 transition-colors"
                                >
                                    <Users size={13} /> {t('contactsMenuNewTeam')}
                                </button>
                                <button
                                    data-testid="hub-menu-blocklist"
                                    onClick={() => { setMenuOpen(false); setShowBlockList(true); }}
                                    className="w-full text-left px-3 py-2 text-xs text-pai-secondary hover:text-white hover:bg-white/[0.07] flex items-center gap-2 transition-colors"
                                >
                                    <Ban size={13} /> {t('contactsMenuBlockList')}
                                </button>
                            </div>
                        )}
                    </div>

                    {onToggleExpand && (
                        <button
                            onClick={onToggleExpand}
                            className="pai-tap p-1.5 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors shrink-0"
                            title={mode === 'expanded' ? 'Collapse to split view' : 'Expand to cover chat'}
                        >
                            {mode === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                    )}
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="pai-tap p-1.5 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors shrink-0"
                            title={t('auxClosePanelEsc', 'Close panel (Esc)')}
                        >
                            <XIcon size={18} />
                        </button>
                    )}
                </div>

                {/* ── Scroll body: Ghost Card + tiers ── */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
                    {error && (
                        <div className="bg-pai-red/[0.08] border border-pai-red/40 rounded-ctl p-2.5 text-xs text-pai-red">
                            {error}
                        </div>
                    )}

                    {discovery && (
                        <GhostCard
                            discovery={discovery}
                            busy={ghostBusy}
                            onSend={handleGhostSend}
                            onCancel={handleGhostCancel}
                            onAccept={() => handleGhostRespond('accept')}
                            onDecline={() => handleGhostRespond('decline')}
                            onGoToLinked={handleGhostGoToLinked}
                        />
                    )}

                    {loading && contacts.length === 0 ? (
                        <div className="text-center py-10 text-sm text-white/40">{t('auxLoadingEllipsis', 'Loading…')}</div>
                    ) : (
                        <>
                            {showNetwork && (
                                <TierSection
                                    tierKey="network"
                                    title={t('tierNetwork')}
                                    count={networkRows.length}
                                    collapsed={!!collapsedTiers.network}
                                    onToggleCollapse={() => toggleTier('network')}
                                    forceExpanded={searching}
                                    emptyLabel={t('tierNetworkEmpty')}
                                    rows={networkRows}
                                    sortMode={sortMode}
                                    renderRow={renderRow}
                                />
                            )}
                            {showTeams && (
                                <TierSection
                                    tierKey="teams"
                                    title={t('tierTeams')}
                                    count={teamRows.length}
                                    collapsed={!!collapsedTiers.teams}
                                    onToggleCollapse={() => toggleTier('teams')}
                                    forceExpanded={searching}
                                    emptyLabel={t('tierTeamsEmpty')}
                                    rows={teamRows}
                                    sortMode={sortMode}
                                    renderRow={renderTeamRow}
                                />
                            )}
                            {showRolodex && (
                                <TierSection
                                    tierKey="rolodex"
                                    title={t('tierRolodex')}
                                    count={rolodexRows.length}
                                    collapsed={!!collapsedTiers.rolodex}
                                    onToggleCollapse={() => toggleTier('rolodex')}
                                    forceExpanded={searching}
                                    emptyLabel={t('tierRolodexEmpty')}
                                    rows={rolodexRows}
                                    sortMode={sortMode}
                                    renderRow={renderRow}
                                />
                            )}

                            {searching && !showNetwork && !showTeams && !showRolodex && !discovery && (
                                <p className="text-center py-8 text-sm text-white/40">{t('contactsNoMatch')}</p>
                            )}
                            {noContactsAtAll && !searching && (
                                <p className="text-center py-8 text-sm text-white/40">{t('contactsEmptyAll')}</p>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer action bar (handoff §7) — sits outside the scroll area.
                    Purely additive: both buttons drive the SAME handlers the
                    command-bar three-dots menu already exposes (no new state, no new
                    behaviour). "Import" from the handoff is deliberately omitted —
                    no import flow exists to wire it to. ── */}
                <div className="flex-shrink-0 flex items-center gap-2.5 px-4 pb-4 pt-1">
                    <button
                        type="button"
                        data-testid="hub-footer-new-contact"
                        onClick={() => setShowCreate(true)}
                        className="pai-btn-primary"
                    >
                        <UserPlus size={15} /> {t('contactsMenuNewContact')}
                    </button>
                    <button
                        type="button"
                        data-testid="hub-footer-new-team"
                        onClick={() => setShowCreateTeam(true)}
                        className="pai-btn-secondary"
                    >
                        <Users size={15} /> {t('contactsMenuNewTeam')}
                    </button>
                </div>

                {/* Relationship-management edit overlay (covers the panel body). */}
                {editingContact && (
                    <ContactEditOverlay
                        contact={editingContact}
                        onClose={() => setEditingContact(null)}
                        onSaved={() => { fetchContacts(); }}
                        onDeleted={() => { setExpandedId(null); fetchContacts(); }}
                        onSevered={() => { setExpandedId(null); fetchContacts(); fetchPendingCount(); }}
                    />
                )}
            </div>

            <ManualContactDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onSaved={() => { fetchContacts(); }}
            />

            <TeamCreateDialog
                open={showCreateTeam}
                networkContacts={networkContacts}
                onClose={() => setShowCreateTeam(false)}
                onCreated={() => { fetchTeams(); }}
            />

            {showPending && (
                <PendingPortal
                    onClose={() => { setShowPending(false); fetchPendingCount(); }}
                    onChanged={() => { fetchContacts(); fetchPendingCount(); }}
                />
            )}

            {showBlockList && (
                <BlockListModal
                    onClose={() => setShowBlockList(false)}
                    onChanged={() => { fetchContacts(); }}
                />
            )}

            {previewDoc && previewDoc.id && (
                <DocumentPreviewModal
                    documentId={previewDoc.id}
                    documentName={previewDoc.filename}
                    onClose={() => setPreviewDoc(null)}
                />
            )}
        </PanelShell>
    );
};

export default AgendaPanel;
